import {
  AHIcon,
  AHSeparator,
  AHText,
  Box,
  Card,
  Circle,
  ListItem,
  useBoxStyle,
  TouchableBox,
} from "arkh";
import React, { useState } from "react";
import { FlatList, View } from "react-native";
import { graphql, useFragment } from "react-relay";

import { useIntlTokens } from "../../components/mobile/useIntlTokens";

import {
  ListSectionHeader,
  ListSectionHeaderTitle,
} from "../../components/atoms/ListSectionHeader";
import { EmployeeScheduleUnAssignedTypesWarningText } from "./getEmployeeScheduleUnAssignedTypesWarningText";
import { useModalInsets } from "../../components/mobile/useModalInsets";
import { EmployeeScheduleEditScheduleListItemAssignedScheduleType_employeeScheduleType$key } from "./__generated__/EmployeeScheduleEditScheduleListItemAssignedScheduleType_employeeScheduleType.graphql";
import { EmployeeScheduleEditScheduleListItem_scheduleTags$key } from "./__generated__/EmployeeScheduleEditScheduleListItem_scheduleTags.graphql";
import { EmployeeScheduleEditScheduleListItem_team$key } from "./__generated__/EmployeeScheduleEditScheduleListItem_team.graphql";
import { EmployeeScheduleEditScheduleList_store$key } from "./__generated__/EmployeeScheduleEditScheduleList_store.graphql";
import { TeamIconCircle } from "../../components/mobile/TeamIconCircle";
import { SelectSecondaryScheduleTypeDimension } from "./SelectSecondaryScheduleTypeDimension";
import { FormattedMessage } from "react-intl";

export function EmployeeScheduleEditScheduleList(props: {
  store: EmployeeScheduleEditScheduleList_store$key;
  onPress?: (teamId: string, teamName: string) => void;
}) {
  const insets = useModalInsets();
  const contentContainerStyle = useBoxStyle({
    space: "medium",
    spaceVertical: "large",
    insets,
  });
  const store = useFragment(
    graphql`
      fragment EmployeeScheduleEditScheduleList_store on Store {
        integrationTilTid {
          secondaryScheduleTypeOptions {
            id
          }
          secondaryScheduleTypeOption {
            id
            name
          }
          ...SelectSecondaryScheduleTypeDimension_integrationTilTid
        }
        allTeams {
          id
          name
          ...EmployeeScheduleEditScheduleListItem_team
        }
        employeeScheduleTypes {
          id
          assignedTeams {
            id
          }
          ...EmployeeScheduleEditScheduleListItemAssignedScheduleType_employeeScheduleType
          ...EmployeeScheduleEditScheduleListItem_scheduleTags
        }
      }
    `,
    props.store
  );

  const unAssignedTypes = store.employeeScheduleTypes.filter(
    (type) => type.assignedTeams.length === 0
  );

  const intl_tokens = useIntlTokens();
  const [
    showSelectSecondaryScheduleTypeDimension,
    setShowSelectSecondaryScheduleTypeDimension,
  ] = useState(false);

  return (
    <>
      <FlatList
        contentContainerStyle={contentContainerStyle}
        data={store.allTeams}
        ItemSeparatorComponent={AHSeparator}
        keyExtractor={(item) => item.id}
        ListHeaderComponent={
          <>
            {store.integrationTilTid &&
            store.integrationTilTid.secondaryScheduleTypeOptions.length > 0 ? (
              <Box spaceBottom="medium">
                <ListItem
                  isFirst
                  isLast
                  disabled
                  right={
                    <TouchableBox
                      borderColor="border-primary"
                      space="small"
                      cornerRadius="small"
                      onPress={() =>
                        setShowSelectSecondaryScheduleTypeDimension(true)
                      }
                    >
                      <Box
                        style={{
                          borderWidth: 1,
                          alignItems: "center",
                          flexDirection: "row",
                          justifyContent: "space-between",
                        }}
                        spaceVertical="xsmall"
                        cornerRadius="medium"
                        spaceHorizontal="small"
                        borderColor="border-primary"
                      >
                        <AHText>
                          {store.integrationTilTid.secondaryScheduleTypeOption
                            ?.name ?? (
                            <FormattedMessage
                              description="EmployeeScheduleEditScheduleList No sub-schedule selected"
                              defaultMessage="No sub-schedule selected"
                            />
                          )}
                        </AHText>
                      </Box>
                    </TouchableBox>
                  }
                >
                  <ListItem.Title>
                    <FormattedMessage
                      description="EmployeeScheduleEditScheduleList Secondary schedule type title"
                      defaultMessage="Sub-schedule"
                    />
                  </ListItem.Title>
                </ListItem>
              </Box>
            ) : null}
            {unAssignedTypes.length > 0 ? (
              <>
                <Card defaultBorder>
                  <Box style={{ flex: 1 }}>
                    <Box
                      spaceBottom="small"
                      style={{ flexDirection: "row", alignItems: "center" }}
                    >
                      <AHIcon
                        type="issue"
                        color="negative"
                        spaceRight="xsmall"
                      />
                      <AHText type="subtitle">
                        <EmployeeScheduleUnAssignedTypesWarningText
                          unAssignedTypes={unAssignedTypes.length}
                        />
                      </AHText>
                    </Box>
                    <View
                      style={{
                        flexDirection: "row",
                        flexWrap: "wrap",
                        flex: 1,
                      }}
                    >
                      {unAssignedTypes.map((tag) => (
                        <EmployeeScheduleEditScheduleListItemAssignedScheduleType
                          key={tag.id}
                          scheduleType={tag}
                        />
                      ))}
                    </View>
                  </Box>
                </Card>
                <Box spaceTop="medium" />
              </>
            ) : null}
            <ListSectionHeader>
              <ListSectionHeaderTitle>
                {intl_tokens.Teams}
              </ListSectionHeaderTitle>
            </ListSectionHeader>
          </>
        }
        renderItem={({ item, index }) => (
          <EmployeeScheduleEditScheduleListItem
            onPress={() => props.onPress?.(item.id, item.name)}
            isFirst={index === 0}
            isLast={store.allTeams.length === index + 1}
            team={item}
            assignedTypes={store.employeeScheduleTypes.filter((type) =>
              type.assignedTeams.some((t) => t.id === item.id)
            )}
          />
        )}
      />
      {store.integrationTilTid ? (
        <SelectSecondaryScheduleTypeDimension
          integrationTilTid={store.integrationTilTid}
          visible={showSelectSecondaryScheduleTypeDimension}
          onClose={() => setShowSelectSecondaryScheduleTypeDimension(false)}
        />
      ) : null}
    </>
  );
}

function EmployeeScheduleEditScheduleListItem(props: {
  isFirst?: boolean;
  isLast?: boolean;
  team: EmployeeScheduleEditScheduleListItem_team$key;
  assignedTypes: EmployeeScheduleEditScheduleListItem_scheduleTags$key;
  onPress?: () => void;
}) {
  const team = useFragment(
    graphql`
      fragment EmployeeScheduleEditScheduleListItem_team on Team {
        name
        ...TeamIconCircleComponent_Team
      }
    `,
    props.team
  );
  const scheduleTags = useFragment(
    graphql`
      fragment EmployeeScheduleEditScheduleListItem_scheduleTags on EmployeeScheduleType
      @relay(plural: true) {
        id
        ...EmployeeScheduleEditScheduleListItemAssignedScheduleType_employeeScheduleType
      }
    `,
    props.assignedTypes
  );

  return (
    <ListItem
      onPress={props.onPress}
      isFirst={props.isFirst}
      border="border-primary"
      isLast={props.isLast}
      left={<TeamIconCircle team={team} size="smallMedium" />}
      right={<ListItem.NavArrow />}
    >
      <ListItem.Title>{team.name}</ListItem.Title>
      {scheduleTags.length > 0 ? (
        <Box
          spaceTop="xxsmall"
          style={{ flexDirection: "row", flexWrap: "wrap", flex: 1 }}
        >
          {scheduleTags.map((tag) => (
            <EmployeeScheduleEditScheduleListItemAssignedScheduleType
              key={tag.id}
              scheduleType={tag}
            />
          ))}
        </Box>
      ) : null}
    </ListItem>
  );
}
function EmployeeScheduleEditScheduleListItemAssignedScheduleType(props: {
  scheduleType: EmployeeScheduleEditScheduleListItemAssignedScheduleType_employeeScheduleType$key;
}) {
  const fragmentType = useFragment(
    graphql`
      fragment EmployeeScheduleEditScheduleListItemAssignedScheduleType_employeeScheduleType on EmployeeScheduleType {
        id
        title
        color
      }
    `,
    props.scheduleType
  );

  return (
    <Box
      style={{ flexDirection: "row", alignItems: "center" }}
      spaceRight="small"
    >
      <Circle
        style={{ backgroundColor: fragmentType.color ?? "#000" }}
        size="dot"
      />
      <AHText spaceLeft="xxsmall" type="detail">
        {fragmentType.title}
      </AHText>
    </Box>
  );
}
